/**
 * A worked example of a `FormDefinition`, used by the contracts tests and by the
 * renderer demo in `@platform/ui`.
 *
 * It is modelled on the shape of HeyAllergy's current Jotform intake - the Notice of
 * Privacy Practices acknowledgement first, then patient, contact, allergy history,
 * insurance and a typed signature - and on the copy captured in
 * `apps/patient/parity/form/`. It reproduces *structure and field types only*.
 *
 * THIS IS NOT THE PRODUCTION FORM. The real HeyAllergy v1 (about 120 fields) is built
 * field-by-field from the client's Jotform export in M8.6 and signed off against a
 * checklist (FR-INTAKE-02); until then nothing here should be seeded into a tenant.
 * No real patient data appears in this file.
 */
export const sampleIntakeV1 = {
    id: 'sample-intake-v1',
    key: 'sample-new-patient-intake',
    title: 'New patient intake',
    description: 'About 5 minutes. You can save and come back at any time.',
    version: 1,
    locale: 'en-US',
    settings: {
        submitLabel: 'Submit intake',
        allowPartialSave: true,
        showProgress: true,
    },
    sections: [
        {
            id: 'privacy',
            title: 'Notice of Privacy Practices',
            description: 'Please read and acknowledge before you continue.',
            fields: [
                {
                    id: 'nppIntro',
                    type: 'paragraph',
                    text: 'This notice describes how your health information may be used and disclosed by the practice, and how you can get access to this information. The full notice is published on our website and available at our clinic locations.',
                },
                {
                    id: 'nppAck',
                    type: 'consent',
                    label: 'Notice of Privacy Practices',
                    text: 'I acknowledge that I have received and reviewed the Notice of Privacy Practices.',
                    required: true,
                },
            ],
        },
        {
            id: 'patient',
            title: 'Patient details',
            fields: [
                {
                    id: 'firstName',
                    type: 'text',
                    label: 'First name',
                    required: true,
                    width: 'half',
                    sensitive: true,
                    phi: true,
                    validation: { pattern: 'name' },
                },
                {
                    id: 'lastName',
                    type: 'text',
                    label: 'Last name',
                    required: true,
                    width: 'half',
                    sensitive: true,
                    phi: true,
                    validation: { pattern: 'name' },
                },
                {
                    id: 'dateOfBirth',
                    type: 'date',
                    label: 'Date of birth',
                    required: true,
                    width: 'half',
                    sensitive: true,
                    phi: true,
                    validation: { minDate: '1900-01-01' },
                },
                {
                    id: 'sexAtBirth',
                    type: 'select',
                    label: 'Sex assigned at birth',
                    required: true,
                    width: 'half',
                    phi: true,
                    options: [
                        { value: 'female', label: 'Female' },
                        { value: 'male', label: 'Male' },
                        { value: 'intersex', label: 'Intersex' },
                        { value: 'undisclosed', label: 'Prefer not to say' },
                    ],
                },
                {
                    id: 'isMinor',
                    type: 'yesNo',
                    label: 'Is the patient under 18?',
                    required: true,
                },
            ],
        },
        {
            id: 'guardian',
            title: 'Parent or guardian',
            description: 'We need a responsible adult on file for patients under 18.',
            visibleWhen: { field: 'isMinor', op: 'eq', value: true },
            fields: [
                {
                    id: 'guardianName',
                    type: 'text',
                    label: 'Parent or guardian name',
                    required: true,
                    sensitive: true,
                    phi: true,
                    validation: { pattern: 'name' },
                },
                {
                    id: 'guardianRelationship',
                    type: 'radio',
                    label: 'Relationship to patient',
                    required: true,
                    options: [
                        { value: 'parent', label: 'Parent' },
                        { value: 'guardian', label: 'Legal guardian' },
                        { value: 'other', label: 'Other' },
                    ],
                },
            ],
        },
        {
            id: 'contact',
            title: 'Contact details',
            fields: [
                {
                    id: 'email',
                    type: 'email',
                    label: 'Email',
                    placeholder: 'you@example.com',
                    required: true,
                    width: 'half',
                    phi: true,
                },
                {
                    id: 'phone',
                    type: 'phone',
                    label: 'Mobile phone',
                    helpText: 'We text appointment reminders to this number.',
                    required: true,
                    width: 'half',
                    phi: true,
                },
                {
                    id: 'state',
                    type: 'select',
                    label: 'State of residence',
                    helpText: 'We can only treat patients located in these states.',
                    required: true,
                    width: 'half',
                    options: [
                        { value: 'CA', label: 'California' },
                        { value: 'FL', label: 'Florida' },
                        { value: 'IL', label: 'Illinois' },
                        { value: 'NV', label: 'Nevada' },
                        { value: 'NY', label: 'New York' },
                        { value: 'PA', label: 'Pennsylvania' },
                        { value: 'TX', label: 'Texas' },
                    ],
                },
                {
                    id: 'postalCode',
                    type: 'text',
                    label: 'ZIP code',
                    required: true,
                    width: 'half',
                    validation: { pattern: 'zipUs' },
                },
                {
                    id: 'smsOptIn',
                    type: 'checkbox',
                    label: 'Text me appointment reminders',
                    helpText: 'Message and data rates may apply. You can turn this off later.',
                },
                {
                    id: 'preferredContact',
                    type: 'radio',
                    label: 'Best way to reach you',
                    required: true,
                    options: [
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone call' },
                        { value: 'text', label: 'Text message' },
                    ],
                },
            ],
        },
        {
            id: 'history',
            title: 'Allergy history',
            fields: [
                {
                    id: 'historyHeading',
                    type: 'heading',
                    text: 'Tell us what we are treating',
                    level: 3,
                },
                {
                    id: 'allergyTypes',
                    type: 'checkboxGroup',
                    label: 'Which allergies affect you?',
                    helpText: 'Choose everything that applies.',
                    required: true,
                    sensitive: true,
                    phi: true,
                    options: [
                        { value: 'food', label: 'Food' },
                        {
                            value: 'environmental',
                            label: 'Environmental',
                            description: 'Pollen, dust, mould, pets',
                        },
                        { value: 'drug', label: 'Medication' },
                        { value: 'insect', label: 'Insect stings' },
                        { value: 'skin', label: 'Skin: eczema or hives' },
                        { value: 'unknown', label: 'Not sure yet' },
                    ],
                },
                {
                    id: 'foodTriggers',
                    type: 'textarea',
                    label: 'Which foods cause a reaction?',
                    placeholder: 'For example: peanut, cashew, sesame',
                    required: true,
                    sensitive: true,
                    phi: true,
                    rows: 3,
                    visibleWhen: { field: 'allergyTypes', op: 'in', value: ['food'] },
                },
                {
                    id: 'reactionCount',
                    type: 'number',
                    label: 'Reactions in the last 12 months',
                    width: 'half',
                    phi: true,
                    validation: { min: 0, max: 365 },
                },
                {
                    id: 'carriesEpinephrine',
                    type: 'yesNo',
                    label: 'Do you carry an epinephrine auto-injector?',
                    required: true,
                    phi: true,
                },
                {
                    id: 'epinephrineBrand',
                    type: 'text',
                    label: 'Which one?',
                    placeholder: 'For example: EpiPen, Auvi-Q',
                    width: 'half',
                    phi: true,
                    visibleWhen: { field: 'carriesEpinephrine', op: 'eq', value: true },
                },
                {
                    id: 'medications',
                    type: 'textarea',
                    label: 'Current medications',
                    helpText: 'Include doses if you know them. Leave blank if none.',
                    sensitive: true,
                    phi: true,
                    rows: 4,
                },
            ],
        },
        {
            id: 'insurance',
            title: 'Insurance',
            fields: [
                {
                    id: 'hasInsurance',
                    type: 'yesNo',
                    label: 'Will you use insurance for this visit?',
                    required: true,
                },
                {
                    id: 'payerName',
                    type: 'text',
                    label: 'Insurance company',
                    required: true,
                    width: 'half',
                    visibleWhen: { field: 'hasInsurance', op: 'eq', value: true },
                },
                {
                    id: 'memberId',
                    type: 'text',
                    label: 'Member id',
                    helpText: 'Exactly as it appears on your card.',
                    required: true,
                    width: 'half',
                    sensitive: true,
                    phi: true,
                    validation: { pattern: 'memberId' },
                    visibleWhen: { field: 'hasInsurance', op: 'eq', value: true },
                },
                {
                    id: 'insuranceCard',
                    type: 'file',
                    label: 'Photo of your insurance card',
                    helpText: 'Front and back. JPG or PNG, up to 10 MB each.',
                    accept: ['image/jpeg', 'image/png', 'image/heic'],
                    maxSizeMb: 10,
                    multiple: true,
                    phi: true,
                    visibleWhen: { field: 'hasInsurance', op: 'eq', value: true },
                },
            ],
        },
        {
            id: 'sign',
            title: 'Confirm and sign',
            fields: [
                {
                    id: 'telehealthConsent',
                    type: 'consent',
                    label: 'Telehealth consent',
                    text: 'I consent to receive care by telehealth and understand I may request an in-person visit at any time.',
                    required: true,
                },
                {
                    id: 'signature',
                    type: 'signature',
                    label: 'Signature',
                    statement: 'I confirm the answers above are accurate to the best of my knowledge.',
                    helpText: 'Typing your full name counts as your signature.',
                    required: true,
                    sensitive: true,
                    phi: true,
                },
            ],
        },
    ],
};
//# sourceMappingURL=sample.js.map